import { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  ACCOUNT_LOGO_EXTENSIONS,
  ACCOUNT_LOGO_MIME_TYPES,
  MAX_ACCOUNT_LOGO_DIMENSION_PX,
  MAX_ACCOUNT_LOGO_SIZE_BYTES
} from '@insio/shared-types';
import { GetProfileDocument, RemoveDocumentAttachmentDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  ATTACHMENT_BUSY,
  ATTACHMENT_LIMIT_REACHED,
  ATTACHMENT_REFUSED,
  ATTACHMENT_TOO_LARGE,
  ATTACHMENT_TOO_SLOW,
  uploadAttachment
} from '@/services/attachment.service';
import { readImageSize } from './read-image-size';

/** What the file picker offers: the logo's extensions and MIME types. */
export const ACCOUNT_LOGO_ACCEPT = [...ACCOUNT_LOGO_EXTENSIONS, ...ACCOUNT_LOGO_MIME_TYPES].join(
  ','
);

/** The logo size limit in whole megabytes, as the texts state it. */
export const MAX_ACCOUNT_LOGO_SIZE_MB = MAX_ACCOUNT_LOGO_SIZE_BYTES / (1024 * 1024);

/** Why a picked file is refused before it is sent. */
export const LogoFileRefusal = {
  WRONG_TYPE: 'WRONG_TYPE',
  TOO_LARGE: 'TOO_LARGE',
  UNREADABLE: 'UNREADABLE',
  TOO_MANY_PIXELS: 'TOO_MANY_PIXELS'
} as const;
export type LogoFileRefusal = (typeof LogoFileRefusal)[keyof typeof LogoFileRefusal];

const LOGO_EXTENSIONS: readonly string[] = ACCOUNT_LOGO_EXTENSIONS;
const LOGO_MIME_TYPES: readonly string[] = ACCOUNT_LOGO_MIME_TYPES;

/** The client-side check of a picked file's name, type and size; null when they pass. */
export function logoFileRefusal(file: File): LogoFileRefusal | null {
  const name = file.name.toLowerCase();
  const extensionOk = LOGO_EXTENSIONS.some(extension => name.endsWith(extension));
  // Some systems report no MIME type; the extension and the server's content check decide then.
  const typeOk = !file.type || LOGO_MIME_TYPES.includes(file.type);
  if (!extensionOk || !typeOk) return LogoFileRefusal.WRONG_TYPE;
  if (file.size > MAX_ACCOUNT_LOGO_SIZE_BYTES) return LogoFileRefusal.TOO_LARGE;
  return null;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];

/** Whether the file's first bytes are the signature its extension promises. */
async function hasMatchingSignature(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, PNG_SIGNATURE.length).arrayBuffer());
  const signature = file.name.toLowerCase().endsWith('.png') ? PNG_SIGNATURE : JPEG_SIGNATURE;
  return signature.every((byte, index) => head[index] === byte);
}

/** The whole client-side check, the content and decoded size too; null when the file may be sent. */
export async function pickedLogoRefusal(file: File): Promise<LogoFileRefusal | null> {
  const refusal = logoFileRefusal(file);
  if (refusal) return refusal;
  if (!(await hasMatchingSignature(file))) return LogoFileRefusal.WRONG_TYPE;
  const size = await readImageSize(file);
  if (!size) return LogoFileRefusal.UNREADABLE;
  if (size.width > MAX_ACCOUNT_LOGO_DIMENSION_PX || size.height > MAX_ACCOUNT_LOGO_DIMENSION_PX) {
    return LogoFileRefusal.TOO_MANY_PIXELS;
  }
  return null;
}

/** Upload and removal of the organization logo, bound to the account as its one attachment. */
export function useOrganizationLogo(accountId: string, logoId: string | null) {
  const t = useFeatureTranslation('systemSettings.organization.logo');
  const { showSuccess, showError } = useToastHelpers();
  const client = useApolloClient();
  const [removeAttachment] = useMutation(RemoveDocumentAttachmentDocument);
  const [busy, setBusy] = useState(false);

  /** Brings the page's profile in line with the server, whichever way the change went. */
  const refetchProfile = async () => {
    try {
      await client.refetchQueries({ include: [GetProfileDocument] });
    } catch {
      // The profile query reports its own failure; the change itself was already announced.
    }
  };

  const uploadErrorMessage = (error: unknown): string => {
    const code = error instanceof Error ? error.message : null;
    switch (code) {
      case ATTACHMENT_REFUSED:
        return t('errors.refused', { px: MAX_ACCOUNT_LOGO_DIMENSION_PX });
      case ATTACHMENT_TOO_LARGE:
        return t('errors.tooLarge', { max: MAX_ACCOUNT_LOGO_SIZE_MB });
      case ATTACHMENT_LIMIT_REACHED:
        return t('errors.limitReached');
      case ATTACHMENT_BUSY:
        return t('errors.busy');
      case ATTACHMENT_TOO_SLOW:
        return t('errors.tooSlow');
      default:
        return t('errors.uploadFailed');
    }
  };

  const removeCurrent = async (fileId: string) => {
    const { error } = await removeAttachment({ variables: { entityId: accountId, fileId } });
    // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
    if (error) throw error;
  };

  /** The message for a file the client-side check refused. */
  const refusalMessage = (refusal: LogoFileRefusal): string => {
    switch (refusal) {
      case LogoFileRefusal.TOO_LARGE:
        return t('errors.tooLarge', { max: MAX_ACCOUNT_LOGO_SIZE_MB });
      case LogoFileRefusal.TOO_MANY_PIXELS:
        return t('errors.refused', { px: MAX_ACCOUNT_LOGO_DIMENSION_PX });
      case LogoFileRefusal.WRONG_TYPE:
      case LogoFileRefusal.UNREADABLE:
        return t('errors.wrongType');
    }
  };

  /**
   * Sends the picked file; an existing logo is removed first, as the account holds only one.
   * The file is checked first, but the server may still refuse it after the old logo is gone.
   */
  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    // Only a file that was sent can have changed what the server holds.
    let sent = false;
    try {
      const refusal = await pickedLogoRefusal(file);
      if (refusal) {
        showError(refusalMessage(refusal));
        return;
      }
      sent = true;
      if (logoId) {
        try {
          await removeCurrent(logoId);
        } catch {
          showError(t('errors.removeFailed'));
          return;
        }
      }
      try {
        await uploadAttachment(accountId, file);
        showSuccess(t('uploaded'));
      } catch (error) {
        showError(uploadErrorMessage(error));
      }
    } finally {
      if (sent) await refetchProfile();
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!logoId) return;
    setBusy(true);
    try {
      await removeCurrent(logoId);
      showSuccess(t('removed'));
    } catch {
      showError(t('errors.removeFailed'));
    } finally {
      await refetchProfile();
      setBusy(false);
    }
  };

  return { t, busy, upload, remove };
}
